import { fetchHelperHealth, fetchHelperPorts, resolveHelperConnectionState } from '@/features/serial/helperClient';
import { fetchLocalDevPorts } from '@/features/serial/localPortClient';
import {
  authorizeWebSerialDevice,
  closeWebSerialPort,
  isWebSerialSupported,
  listWebSerialPorts,
  openWebSerialPort,
  releaseAllWebSerialPortsForUpload,
  writeWebSerial,
} from '@/features/serial/webSerialTransport';

export interface SerialPortOption {
  id: string;
  label: string;
  source: 'helper' | 'local' | 'web-serial';
}

export async function listAvailablePorts(): Promise<{
  ports: SerialPortOption[];
  helperOnline: boolean;
  helperSetupRequired: boolean;
}> {
  const options: SerialPortOption[] = [];
  const seen = new Set<string>();
  let helperOnline = false;
  let helperSetupRequired = false;

  const health = await fetchHelperHealth();
  const connection = resolveHelperConnectionState(health);

  if (connection === 'ready') {
    helperOnline = true;
  } else if (connection === 'setup_required') {
    helperSetupRequired = true;
  }

  if (health?.token && (helperOnline || helperSetupRequired)) {
    try {
      const helperPorts = await fetchHelperPorts(health.token);
      for (const helperPort of helperPorts) {
        const id = `helper:${helperPort.path}`;
        if (seen.has(id)) {
          continue;
        }
        seen.add(id);
        options.push({
          id,
          label: helperPort.label ?? helperPort.path,
          source: 'helper',
        });
      }
    } catch {
      // Helper is online but port listing failed — fall back to local/Web Serial.
    }
  }

  if (!helperOnline && !helperSetupRequired) {
    const localPorts = await fetchLocalDevPorts();
    for (const localPort of localPorts) {
      const id = `local:${localPort.path}`;
      if (seen.has(id)) {
        continue;
      }
      seen.add(id);
      options.push({
        id,
        label: localPort.label ?? localPort.path,
        source: 'local',
      });
    }
  }

  if (isWebSerialSupported()) {
    const webPorts = await listWebSerialPorts();
    for (const webPort of webPorts) {
      if (seen.has(webPort.id)) {
        continue;
      }
      seen.add(webPort.id);
      options.push({
        id: webPort.id,
        label: webPort.label,
        source: 'web-serial',
      });
    }
  }

  return { ports: options, helperOnline, helperSetupRequired };
}

export async function connectSerial(
  portId: string | null,
  baudRate: number,
  onReceive: (text: string) => void,
): Promise<{ portId: string; label: string }> {
  if (!isWebSerialSupported()) {
    throw new Error('Web Serial is not supported in this browser. Use Chrome or Edge.');
  }

  // local:/helper: ids are for display only — reuse the authorized Web Serial device.
  const webSerialPortId =
    portId && !portId.startsWith('helper:') && !portId.startsWith('local:') ? portId : null;

  return openWebSerialPort(webSerialPortId, baudRate, onReceive);
}

export async function disconnectSerial(): Promise<void> {
  await closeWebSerialPort();
}

export async function releaseSerialForUpload(): Promise<void> {
  await releaseAllWebSerialPortsForUpload();
}

export async function writeSerial(data: string): Promise<void> {
  await writeWebSerial(data);
}

export async function addAuthorizedWebSerialDevice(): Promise<{ portId: string; label: string }> {
  const device = await authorizeWebSerialDevice();
  return { portId: device.id, label: device.label };
}
